// -----------------------------------------------------------------------------
// storage/projects.ts — save / load / recent projects.
//
// A project file is the JSON from core/serialize.ts. We:
//   • write it to scratch storage (so it can be re-opened in-browser) AND offer a
//     file download (so it's portable / backup-able),
//   • keep a short "recent projects" list in localStorage,
//   • read a project back either from scratch (recents) or a picked file.
//
// Re-hydrating the media bytes after a load is the app's job (it reads each
// asset's scratchKey via ScratchStore and re-registers a MediaSource).
// -----------------------------------------------------------------------------
import type { MediaAsset, Project } from '../core/model';
import { type SavedDocument, deserializeProject, serializeProject } from '../core/serialize';
import type { ScratchStore } from './scratch';

const RECENTS_KEY = 'zeroframe.recents.v1';
const projectKey = (id: string): string => `project:${id}`;

export interface RecentEntry {
  id: string;
  name: string;
  updatedAt: number;
}

export async function saveProject(
  project: Project,
  assets: MediaAsset[],
  scratch: ScratchStore,
  download = true,
): Promise<void> {
  const json = serializeProject(project, assets);
  await scratch.put(projectKey(project.id), new Blob([json], { type: 'application/json' }));
  addRecent({ id: project.id, name: project.name, updatedAt: Date.now() });
  if (download) downloadText(json, `${safeName(project.name)}.zeroframe.json`);
}

export async function loadProjectFromScratch(
  id: string,
  scratch: ScratchStore,
): Promise<SavedDocument | null> {
  const blob = await scratch.get(projectKey(id));
  if (!blob) return null;
  return deserializeProject(await blob.text());
}

export async function loadProjectFromFile(): Promise<SavedDocument | null> {
  const file = await pickFile('.json,application/json');
  if (!file) return null;
  return deserializeProject(await file.text());
}

export function getRecents(): RecentEntry[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENTS_KEY) ?? '[]');
    return Array.isArray(parsed) ? (parsed as RecentEntry[]) : [];
  } catch {
    return [];
  }
}

function addRecent(entry: RecentEntry): void {
  const list = getRecents().filter((e) => e.id !== entry.id);
  list.unshift(entry);
  localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, 10)));
}

// ---- helpers -----------------------------------------------------------------

function downloadText(text: string, fileName: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function pickFile(accept: string): Promise<File | null> {
  return new Promise<File | null>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.addEventListener('change', () => resolve(input.files?.[0] ?? null), { once: true });
    input.addEventListener('cancel', () => resolve(null), { once: true });
    input.click();
  });
}

function safeName(name: string): string {
  return (name || 'project').trim().replace(/[\\/:*?"<>|\s]+/g, '-').slice(0, 80) || 'project';
}
